/**
 * IDDLAIEvaluateAgent — Phase 6 · Agentic AI Evaluation renderer.
 *
 * The scripted agent produced an OUTPUT plus its full AUDIT TRAIL
 * (tool calls, tool results). The candidate must inspect the trail
 * step-by-step, check each load-bearing claim against what the tool
 * actually returned, flag fabricated/misused steps, and reach a
 * final decision.
 *
 * Scenario schema:
 *
 *   {
 *     task_brief: string,
 *     agent_profile: { name, role, tools: [string] },
 *     scripted_agent_run: [
 *       { step: number, tool: string, query: string, result: string,
 *         supports_claims: [claim_id], fabricated?: bool, misused?: string }
 *     ],
 *     ai_output: {
 *       title: string, body: string,
 *       claims: [{ id, statement, supported_by, fabricated? }],
 *       required_trail_steps: [{ id: "step_<N>" }]
 *     }
 *   }
 *
 * Candidate-attributed events:
 *   evaluate_agent_workspace_opened  — mount
 *   audit_trail_opened               — first look at the trail panel
 *   trail_step_inspected             — { step_id }
 *   trail_step_flagged               — { step_id, reason }
 *   claim_evidence_checked           — { claim_id, verdict }
 *   concern_flagged                  — { claim_id | step_id, reason }
 *   verification_requested           — { target, id }
 *   reliance_decision_made           — { decision }
 *   final_decision                   — { decision }
 *
 * Agent-attributed (revealed as part of the trace context): none
 * emitted at runtime — the trail is scenario data.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { GitBranch, Search, Flag, ShieldCheck, CheckCircle2, RefreshCcw, XCircle, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function IDDLAIEvaluateAgent({
  attemptId, conceptId, scenarioText,
  environment = "agentic_ai",
  onEventLogged,
}) {
  const brief = scenarioText?.task_brief || "Audit the agent's execution trail and output.";
  const profile = scenarioText?.agent_profile || {};
  const trail = scenarioText?.scripted_agent_run || [];
  const output = scenarioText?.ai_output || null;
  const claims = output?.claims || [];
  const requiredStepIds = new Set((output?.required_trail_steps || []).map((s) => s.id));

  const stepId = (n) => `step_${n}`;

  const [trailOpened, setTrailOpened] = useState(false);
  const [inspected, setInspected] = useState(new Set());
  const [flaggedSteps, setFlaggedSteps] = useState(new Set());
  const [claimChecks, setClaimChecks] = useState({});
  const [flaggedClaims, setFlaggedClaims] = useState(new Set());
  const [reliance, setReliance] = useState(null);
  const [finalized, setFinalized] = useState(null);
  const [busy, setBusy] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("evaluate_agent_workspace_opened", null, "evaluate_agent_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext, actor = "candidate") {
    try {
      const r = await api.post(`/attempts/${attemptId}/events`, {
        concept_id: conceptId,
        environment,
        event_name: eventName, event_value: eventValue,
        screen_context: screenContext, actor,
        client_ts_ms: Date.now(),
      }).then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} rejected: ${detail}`);
      return null;
    }
  }

  const openTrail = async () => {
    if (trailOpened) return;
    setTrailOpened(true);
    await emit("audit_trail_opened", null, "trail");
  };

  const inspectStep = async (step) => {
    const id = stepId(step.step);
    if (inspected.has(id)) return;
    await emit("trail_step_inspected", { step_id: id }, "trail");
    setInspected((s) => new Set([...s, id]));
  };

  const flagStep = async (step) => {
    const id = stepId(step.step);
    if (flaggedSteps.has(id)) return;
    await emit("trail_step_flagged", { step_id: id, reason: step.fabricated ? "fabricated_result" : step.misused ? "misused_result" : "attention" }, "trail");
    setFlaggedSteps((s) => new Set([...s, id]));
  };

  const checkClaim = async (claim, verdict) => {
    await emit("claim_evidence_checked", { claim_id: claim.id, verdict }, "claims");
    setClaimChecks((m) => ({ ...m, [claim.id]: verdict }));
  };

  const flagClaim = async (claim) => {
    if (flaggedClaims.has(claim.id)) return;
    await emit("concern_flagged", { claim_id: claim.id, reason: "unsupported" }, "flag");
    setFlaggedClaims((s) => new Set([...s, claim.id]));
  };

  const setRelianceAndEmit = async (decision) => {
    setReliance(decision);
    await emit("reliance_decision_made", { decision }, "reliance");
  };

  const finalize = async (decision) => {
    if (busy || finalized) return;
    setBusy(true);
    try {
      await emit("final_decision", {
        decision, reliance, inspected_count: inspected.size,
      }, "finalize");
      setFinalized(decision);
      toast.success(`Recorded: ${decision}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="iddl-ai-evaluate-agent">
      {/* Left: brief + reliance + finalize */}
      <section className="md:col-span-1 border border-[var(--iddl-rule)] bg-white">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <ShieldCheck size={14} />
          <span className="font-display font-semibold">Audit task</span>
        </header>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-6 whitespace-pre-wrap" data-testid="evaluate-agent-brief">{brief}</p>
          <div className="text-xs text-zinc-600">
            <div><strong>Agent:</strong> {profile.name || "—"} · <em>{profile.role || ""}</em></div>
            <div><strong>Tools:</strong> {(profile.tools || []).join(", ") || "—"}</div>
          </div>

          <div className="space-y-2 pt-2 border-t border-[var(--iddl-rule)]">
            <div className="label-mono text-xs">Reliance decision</div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "rely", label: "Rely", cls: "border-emerald-400" },
                { id: "rely_with_verification", label: "After verification", cls: "border-amber-400" },
                { id: "do_not_rely", label: "Do not rely", cls: "border-rose-400" },
              ].map((r) => (
                <button key={r.id} type="button"
                  onClick={() => setRelianceAndEmit(r.id)}
                  className={`text-xs px-3 py-1.5 border ${r.cls} ${reliance === r.id ? "bg-zinc-100 font-semibold" : "bg-white"}`}
                  data-testid={`evaluate-agent-reliance-${r.id}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2 pt-2 border-t border-[var(--iddl-rule)]">
            <div className="label-mono text-xs">Final decision</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "accept", label: "Accept", Icon: CheckCircle2 },
                { id: "revise", label: "Revise", Icon: RefreshCcw },
                { id: "reject", label: "Reject", Icon: XCircle },
                { id: "request_human_review", label: "Human review", Icon: UserCog },
              ].map(({ id, label, Icon }) => (
                <Button key={id} size="sm" variant="outline"
                  disabled={busy || !!finalized}
                  onClick={() => finalize(id)}
                  data-testid={`evaluate-agent-final-${id}`}>
                  <Icon size={14} className="me-1" /> {label}
                </Button>
              ))}
            </div>
            {finalized ? (
              <div className="text-xs text-emerald-800 pt-2">
                Recorded: <strong>{finalized}</strong>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* Middle: audit trail */}
      <section className="md:col-span-1 border border-[var(--iddl-rule)] bg-white" data-testid="evaluate-agent-trail">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm cursor-pointer" onClick={openTrail}>
          <GitBranch size={14} />
          <span className="font-display font-semibold">Agent audit trail</span>
          <span className="ms-auto label-mono text-xs">
            {inspected.size}/{trail.length} inspected
          </span>
        </header>
        <div className="p-3 space-y-2">
          {!trailOpened && (
            <div className="text-xs text-zinc-500 italic">Click the header to reveal the agent's tool calls and results.</div>
          )}
          {trailOpened && trail.map((step) => {
            const id = stepId(step.step);
            const ins = inspected.has(id);
            const flg = flaggedSteps.has(id);
            const required = requiredStepIds.has(id);
            return (
              <div key={step.step}
                className={`border ${flg ? "border-rose-400 bg-rose-50" : ins ? "border-emerald-300 bg-emerald-50/40" : "border-[var(--iddl-rule)]"} p-2`}
                data-testid={`evaluate-agent-step-${step.step}`}>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="label-mono">step {step.step}</span>
                  <code className="font-mono">{step.tool}</code>
                  {required && <span className="label-mono border border-amber-400 px-1">LOAD-BEARING</span>}
                </div>
                <div className="text-xs mt-1"><strong>query:</strong> {step.query}</div>
                <div className="text-xs mt-1"><strong>result:</strong> {step.result}</div>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => inspectStep(step)} disabled={ins}
                    className="text-xs px-2 py-1 border border-emerald-400 disabled:opacity-50"
                    data-testid={`evaluate-agent-step-inspect-${step.step}`}>
                    <Search size={10} className="inline me-1" /> {ins ? "Inspected" : "Inspect"}
                  </button>
                  <button type="button" onClick={() => flagStep(step)} disabled={flg}
                    className="text-xs px-2 py-1 border border-rose-400 disabled:opacity-50"
                    data-testid={`evaluate-agent-step-flag-${step.step}`}>
                    <Flag size={10} className="inline me-1" /> {flg ? "Flagged" : "Flag"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Right: agent output + per-claim checks */}
      <section className="md:col-span-1 border border-[var(--iddl-rule)] bg-white" data-testid="evaluate-agent-output">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <ShieldCheck size={14} />
          <span className="font-display font-semibold">{output?.title || "Agent output"}</span>
        </header>
        <div className="p-3 space-y-3">
          <div className="text-sm leading-6 whitespace-pre-wrap">{output?.body || ""}</div>
          <div className="space-y-2 border-t border-[var(--iddl-rule)] pt-2">
            <div className="label-mono text-xs">Claim-by-claim check</div>
            {claims.map((c) => (
              <div key={c.id} className={`border ${flaggedClaims.has(c.id) ? "border-rose-400" : "border-[var(--iddl-rule)]"} p-2`}
                data-testid={`evaluate-agent-claim-${c.id}`}>
                <div className="text-xs">{c.statement}</div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {["supported", "weak", "missing"].map((v) => (
                    <button key={v} type="button"
                      onClick={() => checkClaim(c, v)}
                      className={`text-[10px] px-2 py-0.5 border ${claimChecks[c.id] === v ? "bg-zinc-100 font-semibold" : ""}`}
                      data-testid={`evaluate-agent-claim-${c.id}-${v}`}>
                      {v}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => flagClaim(c)} disabled={flaggedClaims.has(c.id)}
                    className="text-[10px] px-2 py-0.5 border border-rose-400 disabled:opacity-50"
                    data-testid={`evaluate-agent-claim-flag-${c.id}`}>
                    <Flag size={10} className="inline me-1" /> flag
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
